// src/features/imprint/utils/calendarBundleValidation.js
/**
 * Calendar Bundle Validation Utilities
 * 
 * Validates that all calendars in a bundle can be printed with the same imprint method.
 * Each imprint method has a list of associated SKUs (calendar products it supports).
 * For a bundle to be valid, ALL selected calendar SKUs must be supported by at least one imprint method.
 */

/**
 * Get available imprint methods that support ALL selected calendar SKUs
 * 
 * @param {Object} configurableArray - The configurable_array from full_product JSON
 * @param {Array<string>} selectedSkus - Array of calendar SKUs from the bundle
 * @returns {Array<string>} - Array of method keys that support all selected SKUs
 */
export function getAvailablePrintMethods(configurableArray, selectedSkus) {
    const available = [];

    console.log('[🔍 DEBUG] getAvailablePrintMethods called');
   // console.log('[🔍 DEBUG] configurableArray:', configurableArray);
    //console.log('[🔍 DEBUG] selectedSkus:', selectedSkus);

    if (!configurableArray) {
        console.error('[🔍 DEBUG] configurableArray is null/undefined!');
        return available;
    }
    
    if (!selectedSkus || selectedSkus.length === 0) {
        console.warn('[🔍 DEBUG] selectedSkus is empty, returning all methods as available');
        return Object.keys(configurableArray);
    }

    Object.keys(configurableArray).forEach(methodKey => {
        const method = configurableArray[methodKey];
        
        console.log('[🔍 DEBUG] Checking method:', methodKey);
        console.log('[🔍 DEBUG] method.associated_sku:', method.associated_sku);
        
        // Get SKUs supported by this method
        const methodSKUs = method.associated_sku?.map(item => item.sku) || [];
        
        console.log('[🔍 DEBUG] methodSKUs for', methodKey, ':', methodSKUs);
        
        // Check if ALL selected SKUs are supported by this method
        const allPresent = selectedSkus.every(sku => {
            const found = methodSKUs.includes(sku);
            console.log('[🔍 DEBUG] Checking if', sku, 'is in', methodKey, ':', found);
            return found;
        });

        console.log('[🔍 DEBUG] Method', methodKey, 'supports all SKUs:', allPresent);

        if (allPresent) {
            available.push(methodKey);
        }
    });

    console.log('[🔍 DEBUG] Final available methods:', available);

    return available;
}

/**
 * Check if a specific imprint method is available for the selected calendars
 * 
 * @param {Object} configurableArray - The configurable_array from full_product JSON
 * @param {Array<string>} selectedSkus - Array of calendar SKUs from the bundle
 * @param {string} methodKey - The imprint method key to check
 * @returns {boolean} - True if method can print all selected calendars
 */
export function isMethodAvailableForBundle(configurableArray, selectedSkus, methodKey) {
    if (!configurableArray || !selectedSkus || !methodKey) {
        return false;
    }

    const method = configurableArray[methodKey];
    if (!method) {
        return false;
    }

    const methodSKUs = method.associated_sku?.map(item => item.sku) || [];
    return selectedSkus.every(sku => methodSKUs.includes(sku));
}

/**
 * Validate calendar bundle compatibility
 * Returns detailed validation result
 * 
 * @param {Object} configurableArray - The configurable_array from full_product JSON
 * @param {Array<Object>} calendarProducts - Array of calendar products from store
 * @returns {Object} - Validation result with isValid, availableMethods, and error details
 */
export function validateCalendarBundle(configurableArray, calendarProducts) {
    console.log('[🔍 DEBUG] validateCalendarBundle called');
    console.log('[🔍 DEBUG] calendarProducts:', calendarProducts);
    
    const result = {
        isValid: false,
        availableMethods: [],
        selectedSkus: [],
        errorMessage: null
    };

    // Extract SKUs from calendar products
    result.selectedSkus = calendarProducts
        .map(product => {
            console.log('[🔍 DEBUG] Product:', product.title, 'calendar_sku:', product.calendar_sku);
            return product.calendar_sku;
        })
        .filter(sku => sku);

    console.log('[🔍 DEBUG] Extracted selectedSkus:', result.selectedSkus);

    if (result.selectedSkus.length === 0) {
        console.warn('[🔍 DEBUG] No SKUs found, returning all methods as valid');
        // If no SKUs yet, all methods are available
        result.availableMethods = Object.keys(configurableArray || {});
        result.isValid = result.availableMethods.length > 0;
        return result;
    }

    // Get available methods
    result.availableMethods = getAvailablePrintMethods(configurableArray, result.selectedSkus);

    // Valid if at least one method can print all calendars
    result.isValid = result.availableMethods.length > 0;

    if (!result.isValid) {
        result.errorMessage = 'Unfortunately, one or more calendar titles in your bundle cannot be ordered together. Please remove one or more titles and try again.';
    }

    console.log('[🔍 DEBUG] validateCalendarBundle result:', result);

    return result;
}
